import * as React from "react"
import { Col, Container, Hidden, Row, Visible } from "react-grid-system"
import styled from "styled-components"
import Card from "../../atoms/Card"
import H3 from "../../atoms/typography/H3"
import Subtitle from "../../atoms/typography/Subtitle"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import TextButton from "../../atoms/buttons/TextButton"
import { CheckmarkIcon } from "../../atoms/icons/CheckboxIcons"
import { CrossIcon } from "../../atoms/icons/CrossIcon"
import { EditIcon } from "../../atoms/icons/EditIcon"
import AddressForm, { DEFAULT_SHIPPING_ADDRESS_INFO, updateAddress } from "../../molecules/AddressForm"
import ProfileImageActionable from "../../molecules/ProfileImageActionable"
import TextInput from "../../molecules/TextInput"
import { useUser } from "../../../hooks/useUser"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import { theme } from "../../theme"
import { useUpdateProfileImage } from "../../../hooks/useUpdateProfileImage"
import { useUpdateUserDetails } from "../../../hooks/useUpdateUserDetails"
import { useMyOrganization } from "../../../hooks/useMyOrganization"
import { useUpdateOrgShippingAddress } from "../../../hooks/useUpdateOrgShippingAddress"
import { useLeaveOrganization } from "../../../hooks/useLeaveOrganization"
import { useRemoveUserAccount } from "../../../hooks/useRemoveUserAccount"
import { useRemoveProfileImage } from "../../../hooks/useRemoveProfileImage"
import { MediaQuery } from "../../../utils/design-helpers"
import { useTranslation } from "react-i18next"
import { AnimatePresence } from "framer-motion"
import Modal from "../../molecules/Modals/Modal"
import LeaveOrgModalContent from "../../molecules/Modals/LeaveOrgModalContent"
import DeleteAccountWarningModal from "../../molecules/Modals/DeleteAccountWarningModal"
import AccountDeletedModalContent from "../../molecules/Modals/AccountDeletedModalContent"
import { GraphQLError } from "graphql"
import { ShippingAddress } from "../../../utils/models"
import H2 from "../../atoms/typography/H2"
import SmallButton from "../../atoms/buttons/SmallButton"
import _ from "lodash"
import Body from "../../atoms/typography/Body"
import BodyMedium from "../../atoms/typography/BodyMedium"
import LoginModalContainer from "../../organisms/LoginScreens/LoginModalContainer"
import { APIErrorMessage } from "../../../utils/models"
import { ApolloError, NetworkError } from "@apollo/client/errors"
import { captureExceptionWithContext } from "../../../utils/sentry"

const ProfileImagecontainer = styled.div`
  position: absolute;
  top: 0;
  left: 20%;
  margin-top: -110px;

  ${MediaQuery.XS} {
    left: 5%;
  }
`

const Content = styled.div`
  padding: ${(props) => props.theme.spacings.extreme};
`

interface UpdateUserDetailsInput {
  firstName?: string
  lastName?: string
  mobilePhoneNumber?: string
  title?: string
}

const AccountPage: React.FunctionComponent = () => {
  const { t } = useTranslation()

  const [isEditing, setIsEditing] = React.useState(false)
  const [hasUpdatedUserDetails, setHasUpdatedUserDetails] = React.useState(false)
  const [userDetails, setUserDetails] = React.useState<undefined | UpdateUserDetailsInput>(undefined)
  const [hasUpdatedShippingAddress, setHasUpdatedShippingAddress] = React.useState(false)
  const [shippingAddress, setShippingAddress] = React.useState<undefined | ShippingAddress>(undefined)

  const [leaveOrgModalOpen, setLeaveOrgModalOpen] = React.useState(false)
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = React.useState(false)
  const [accountDeletedModal, setAccountDeletedModal] = React.useState(false)
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = React.useState(false)

  const { user, loading } = useUser()
  const { myOrganization, orgLoading } = useMyOrganization()

  const [savedProfilePic, setSavedProfilePic] = React.useState<string | undefined>()
  const [profilePicFile, setProfilePicFile] = React.useState<File>()

  const [errorMessage, setErrorMessage] = React.useState<string>()
  const [formErrors, setFormErrors] = React.useState<APIErrorMessage[]>()

  const submitRef = React.useRef<HTMLButtonElement>(null)

  const displayedProfilePic = savedProfilePic ?? user?.profile?.imageUrl

  const shippingAddressEmpty = _.isEqual({ ...shippingAddress, country: "" }, DEFAULT_SHIPPING_ADDRESS_INFO)

  React.useMemo(() => {
    !loading &&
      setUserDetails({
        firstName: user?.firstName,
        lastName: user?.lastName,
        title: user?.title,
        mobilePhoneNumber: user?.mobilePhoneNumber,
      })

    !orgLoading && setShippingAddress(updateAddress(DEFAULT_SHIPPING_ADDRESS_INFO, myOrganization?.shippingAddress))
  }, [user, myOrganization])

  const onCompletedUpdateProfileImage = () => {
    setProfilePicFile(undefined)
    setIsEditing(false)
  }
  const onErrorUpdateProfileImage = ({
    graphQLErrors,
    networkError,
  }: {
    graphQLErrors: ReadonlyArray<GraphQLError>
    networkError: NetworkError
  }) => {
    setSavedProfilePic(user?.profile?.imageUrl)
    setProfilePicFile(undefined)
    if (networkError && "statusCode" in networkError && networkError.statusCode === 413) {
      // Special handling is needeed when the application is behind a reverse proxy. Reverse proxies
      // typically doesn't allow arbitrary large body payload so we need to handle this issue
      // in the frontend. Most reverse proxies should return a 413 status code, at least nginx does.
      setErrorMessage("File size too large, max 500kb")
    } else {
      const message = graphQLErrors.map((error) => error.message.replaceAll(/[\[\]\']/g, "")).join("\n")
      setErrorMessage(message)
    }
  }
  const { updateProfileImage } = useUpdateProfileImage({
    onCompleted: onCompletedUpdateProfileImage,
    onError: onErrorUpdateProfileImage,
  })
  const onUpdateProfileImage = async (file: File) => {
    try {
      await updateProfileImage({
        variables: {
          file,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error updating profile image", user?.id)
    }
  }

  const onCompletedUpdateUserDetails = (data: any) => {
    if (data?.updateUserDetails?.errors.length > 0) {
      setFormErrors(data?.updateUserDetails?.errors)
    } else {
      setHasUpdatedUserDetails(false)
    }
  }
  const onErrorUpdateUserDetails = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error updating user details", user?.id)
  }
  const { updateUserDetails } = useUpdateUserDetails({
    onCompleted: (data: any) => onCompletedUpdateUserDetails(data),
    onError: onErrorUpdateUserDetails,
  })
  const onUpdateUserDetails = async () => {
    try {
      await updateUserDetails({
        variables: {
          input: userDetails,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error updating user details", user?.id)
    }
  }

  const onCompletedUpdateShippingAddress = (data: any) => {
    if (data?.updateShippingAddress?.errors.length > 0 || (formErrors?.length ?? 0) > 0) {
      const errors = data?.updateShippingAddress?.errors ?? []
      const userFormErrors = formErrors ?? []
      const mergedErrors = errors.concat(userFormErrors)
      setFormErrors(mergedErrors)
    } else {
      setHasUpdatedShippingAddress(false)
    }
  }
  const onErrorUpdateShippingAddress = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error updating shipping address", user?.id)
  }
  const { updateOrgShippingAddress } = useUpdateOrgShippingAddress({
    onCompleted: (data: any) => onCompletedUpdateShippingAddress(data),
    onError: onErrorUpdateShippingAddress,
  })
  const onUpdateShippingAddress = async (address = shippingAddress) => {
    try {
      await updateOrgShippingAddress({
        variables: {
          input: address,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error updating shipping address", user?.id)
    }
  }

  const onSubmitChanges = async (event?: React.FormEvent) => {
    event?.preventDefault()

    try {
      if (hasUpdatedUserDetails) {
        await onUpdateUserDetails()
      }
      if (hasUpdatedShippingAddress) {
        await onUpdateShippingAddress()
      }
      if (formErrors?.length === 0) {
        setIsEditing(false)
      }
    } catch (error) {
      captureExceptionWithContext(error, "error updating user details", user?.id)
    } finally {
      setFormErrors(undefined)
      setIsEditing(false)
    }
  }

  const onClickCancel = () => {
    setIsEditing(false)
    setFormErrors(undefined)
    setShippingAddress(updateAddress(DEFAULT_SHIPPING_ADDRESS_INFO, myOrganization?.shippingAddress))
    setUserDetails({
      firstName: user?.firstName,
      lastName: user?.lastName,
      title: user?.title,
      mobilePhoneNumber: user?.mobilePhoneNumber,
    })
  }

  const onClickSave = () => {
    if (profilePicFile && savedProfilePic !== user?.profile?.imageUrl) {
      onUpdateProfileImage(profilePicFile)
    }

    if (hasUpdatedShippingAddress || hasUpdatedUserDetails) {
      submitRef.current?.click()
    }
  }

  const onClickCamera = (newProfilePic: string, file: File) => {
    setSavedProfilePic(newProfilePic)
    setProfilePicFile(file)
  }

  const handleChangeUserDetails = (event: any) => {
    const name = event.target.name
    const value = event.target.value

    setUserDetails((values) => ({ ...values, [name]: value }))
    setHasUpdatedUserDetails(true)
  }

  const handleChangeShippingAddress = (event: any) => {
    const name = event.target.name
    const value = event.target.value

    setShippingAddress((values) => ({ ...values, [name]: value }))
    setHasUpdatedShippingAddress(true)
  }

  const onCompletedLeaveOrganization = () => {
    setIsEditing(false)
    setLeaveOrgModalOpen(false)
  }
  const onErrorLeaveOrganization = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, `error leaving organization ${myOrganization?.id}`, user?.id)
  }
  const { leaveOrganization } = useLeaveOrganization({
    onCompleted: onCompletedLeaveOrganization,
    onError: onErrorLeaveOrganization,
  })
  const onLeaveOrganization = async () => {
    try {
      await leaveOrganization({})
    } catch (error) {
      captureExceptionWithContext(error, `error leaving organization ${myOrganization?.id}`, user?.id)
    }
  }

  const configureTwoFactorAuth = () => {
    // simply redirect to the two factor pop up
    window.location.href = "/profile/account/two_factor/"
  }
  const setUpTwoFactorAuth = () => {
    // simply redirect to the two factor pop up
    window.location.href = "/profile/account/two_factor/setup/"
  }

  const onCompletedDeleteAccount = () => {
    setAccountDeletedModal(true)
    setTimeout(() => (window.location.href = "/"), 2000)
  }
  const onErrorDeleteAccount = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error deleting account", user?.id)
  }
  const { removeUserAccount } = useRemoveUserAccount({
    onCompleted: onCompletedDeleteAccount,
    onError: onErrorDeleteAccount,
  })
  const onDeleteAccount = async () => {
    try {
      await removeUserAccount({})
    } catch (error) {
      captureExceptionWithContext(error, "error deleting account", user?.id)
    }
  }

  const onCompletedRemoveProfilePicture = () => {
    setLeaveOrgModalOpen(false)
    setSavedProfilePic(undefined)
  }
  const onErrorRemoveProfilePicture = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error removing profile picture", user?.id)
  }
  const { removeProfileImage } = useRemoveProfileImage({
    onCompleted: onCompletedRemoveProfilePicture,
    onError: onErrorRemoveProfilePicture,
  })
  const onRemoveProfilePicture = async () => {
    try {
      await removeProfileImage({})
    } catch (error) {
      captureExceptionWithContext(error, "error removing profile picture", user?.id)
    }
  }

  const onClickclearShippingAddress = () => {
    setHasUpdatedShippingAddress(true)
    setShippingAddress(DEFAULT_SHIPPING_ADDRESS_INFO)
  }

  return (
    <div>
      <HorizontalSpacer spacing={4} />
      <div style={{ position: "relative" }}>
        <Card>
          <ProfileImagecontainer>
            <div style={{ display: "flex", alignItems: "baseline" }}>
              {loading ? (
                <LoadingSpinner />
              ) : (
                <ProfileImageActionable
                  profilePic={displayedProfilePic}
                  onClickCamera={onClickCamera}
                  onClickDelete={onRemoveProfilePicture}
                  isEditing={isEditing}
                />
              )}
              <Hidden xs>
                <div>
                  {!isEditing && (
                    <PrimaryButton
                      title={t("profile_page.edit_profile")}
                      icon={<EditIcon />}
                      onClick={() => setIsEditing(true)}
                    />
                  )}
                  {isEditing && (
                    <div style={{ display: "flex" }}>
                      <SecondaryButton
                        title={t("common.save")}
                        icon={<CheckmarkIcon color={theme.colors.white} />}
                        type="button"
                        disabled={!(hasUpdatedUserDetails || hasUpdatedShippingAddress || savedProfilePic)}
                        onClick={onClickSave}
                      />
                      <Spacer spacing={1} />
                      <PrimaryButton title={t("common.cancel")} icon={<CrossIcon />} onClick={onClickCancel} />
                    </div>
                  )}
                </div>
              </Hidden>
            </div>
          </ProfileImagecontainer>
          <HorizontalSpacer spacing={12} />
          <Visible xs>
            <div
              style={{
                margin: "1.5rem",
                display: "flex",
                justifyContent: "center",
                marginTop: 80,
              }}
            >
              {!isEditing && (
                <PrimaryButton
                  title={t("profile_page.edit_profile")}
                  icon={<EditIcon />}
                  onClick={() => setIsEditing(true)}
                />
              )}
              {isEditing && (
                <div style={{ display: "flex" }}>
                  <SecondaryButton
                    title="Save"
                    type="button"
                    onClick={onClickSave}
                    icon={<CheckmarkIcon color={theme.colors.white} />}
                    disabled={!(hasUpdatedUserDetails || hasUpdatedShippingAddress || savedProfilePic)}
                  />
                  <Spacer spacing={1} />
                  <PrimaryButton title="Cancel" icon={<CrossIcon />} onClick={() => setIsEditing(false)} />
                </div>
              )}
            </div>
          </Visible>
          <form onSubmit={onSubmitChanges}>
            <button style={{ display: "none" }} type="submit" ref={submitRef} />
            <Content>
              <Row>
                <Col md={2} xs={0} />
                <Col md={3} xs={12}>
                  <Subtitle>About</Subtitle>
                </Col>
              </Row>
              <HorizontalSpacer spacing={2} />
              <Row>
                <Col md={2} xs={0} />
                <Col md={3} xs={12}>
                  {isEditing ? (
                    <>
                      <TextInput
                        textFieldTitle="First name"
                        name="firstName"
                        defaultValue={user?.firstName}
                        onChange={handleChangeUserDetails}
                        required={true}
                      />
                      {formErrors?.find((error) => error.field === "firstName") && (
                        <H3 color={theme.colors.red}>
                          {formErrors
                            ?.find((error) => error.field === "firstName")
                            ?.messages.map((message) => <div key={message}>{message}</div>)}
                        </H3>
                      )}
                    </>
                  ) : (
                    <>
                      <Body>First name:</Body>
                      <HorizontalSpacer spacing={0.5} />
                      <BodyMedium>{user?.firstName}</BodyMedium>
                    </>
                  )}
                  <Visible xs>
                    <HorizontalSpacer spacing={3} />
                  </Visible>
                </Col>
                <Col md={3} xs={12}>
                  {isEditing ? (
                    <>
                      <TextInput
                        textFieldTitle="Last name"
                        name="lastName"
                        defaultValue={user?.lastName}
                        onChange={handleChangeUserDetails}
                        required={true}
                      />
                      {formErrors?.find((error) => error.field === "lastName") && (
                        <H3 color={theme.colors.red}>
                          {formErrors
                            ?.find((error) => error.field === "lastName")
                            ?.messages.map((message) => <div key={message}>{message}</div>)}
                        </H3>
                      )}
                    </>
                  ) : (
                    <>
                      <Body>Last name:</Body>
                      <HorizontalSpacer spacing={0.5} />
                      <BodyMedium>{user?.lastName}</BodyMedium>
                    </>
                  )}
                </Col>
              </Row>
              <HorizontalSpacer spacing={3} />
              <Row>
                <Col md={2} xs={0} />
                <Col md={3} xs={12}>
                  <Body>Organization:</Body>
                  <HorizontalSpacer spacing={0.5} />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                    }}
                  >
                    <BodyMedium>{user?.organization?.name}</BodyMedium>
                    {isEditing && (
                      <TextButton title="Leave" color={theme.colors.red} onClick={() => setLeaveOrgModalOpen(true)} />
                    )}
                  </div>
                  <Visible xs>
                    <HorizontalSpacer spacing={3} />
                  </Visible>
                </Col>
                <Col md={3} xs={12}>
                  <Body>Email:</Body>
                  <HorizontalSpacer spacing={0.5} />
                  <BodyMedium>{user?.email}</BodyMedium>
                </Col>
              </Row>
              <HorizontalSpacer spacing={3} />
              <Row>
                <Col md={2} xs={0} />
                <Col md={3} xs={12}>
                  {isEditing ? (
                    <>
                      <TextInput
                        textFieldTitle="Title"
                        name="title"
                        defaultValue={user?.title}
                        onChange={handleChangeUserDetails}
                        required={false}
                      />
                      {formErrors?.find((error) => error.field === "title") && (
                        <H3 color={theme.colors.red}>
                          {formErrors
                            ?.find((error) => error.field === "title")
                            ?.messages.map((message) => <div key={message}>{message}</div>)}
                        </H3>
                      )}
                    </>
                  ) : (
                    <>
                      <Body>Title:</Body>
                      <HorizontalSpacer spacing={0.5} />
                      <BodyMedium>{user?.title}</BodyMedium>
                    </>
                  )}
                  <Visible xs>
                    <HorizontalSpacer spacing={3} />
                  </Visible>
                </Col>
                <Col md={3} xs={12}>
                  {isEditing ? (
                    <>
                      <TextInput
                        textFieldTitle="Phone"
                        name="mobilePhoneNumber"
                        defaultValue={user?.mobilePhoneNumber}
                        onChange={handleChangeUserDetails}
                        required={false}
                      />
                      {formErrors?.find((error) => error.field === "mobilePhoneNumber") && (
                        <H3 color={theme.colors.red}>
                          {formErrors
                            ?.find((error) => error.field === "mobilePhoneNumber")
                            ?.messages.map((message) => <div key={message}>{message}</div>)}
                        </H3>
                      )}
                    </>
                  ) : (
                    <>
                      <Body>Phone:</Body>
                      <HorizontalSpacer spacing={0.5} />
                      <BodyMedium>{user?.mobilePhoneNumber}</BodyMedium>
                    </>
                  )}
                </Col>
              </Row>

              <HorizontalSpacer spacing={3} />
              <Row>
                <Col md={2} xs={0} />
                <Col md={5} xs={12}>
                  <Container>
                    <Row align="center">
                      <Subtitle>Personal shipping address</Subtitle>
                      <Spacer spacing={1} />
                      {isEditing && (
                        <SmallButton
                          title="Clear"
                          onClick={onClickclearShippingAddress}
                          disabled={shippingAddressEmpty}
                        />
                      )}
                    </Row>
                  </Container>
                  <HorizontalSpacer spacing={3} />
                  {orgLoading ? (
                    <LoadingSpinner />
                  ) : (
                    <AddressForm
                      onChange={handleChangeShippingAddress}
                      disabled={!isEditing}
                      address1Value={shippingAddress?.address1}
                      address2Value={shippingAddress?.address2}
                      cityValue={shippingAddress?.city}
                      countryValue={shippingAddress?.country}
                      zipCodeValue={shippingAddress?.zipCode}
                      requiredFields={!shippingAddressEmpty}
                      hideVat
                    />
                  )}
                </Col>
              </Row>
              <HorizontalSpacer spacing={4} />
              <Row>
                <Col md={2} xs={0} />
                <Col md={3} xs={12}>
                  <Subtitle>Security</Subtitle>
                </Col>
              </Row>
              <HorizontalSpacer spacing={2} />
              <Row>
                <Col md={2} xs={0} />
                <Col md={3} xs={12}>
                  <Body>Two factor authentication:</Body>
                  <HorizontalSpacer spacing={0.5} />
                  <BodyMedium>{user?.hasTotp ? "Enabled" : "Disabled"}</BodyMedium>
                  {user?.hasTotp && <PrimaryButton title="Configure" type="button" onClick={configureTwoFactorAuth} />}
                  {!user?.hasTotp && <PrimaryButton title="Set up" onClick={setUpTwoFactorAuth} type="button" />}
                </Col>
              </Row>
              <HorizontalSpacer spacing={3} />
              <Row>
                <Col md={2} xs={0} />
                <Col md={3} xs={12}>
                  <Body>Password:</Body>
                  <HorizontalSpacer spacing={0.5} />
                  <PrimaryButton type="button" title="Reset password" onClick={() => setResetPasswordModalOpen(true)} />
                </Col>
              </Row>
              <HorizontalSpacer spacing={3} />
              <Row>
                <Col md={2} xs={0} />
                <Col md={3} xs={12}>
                  <TextButton
                    title="Delete account"
                    color={theme.colors.red}
                    onClick={() => setDeleteAccountModalOpen(true)}
                  />
                </Col>
              </Row>
            </Content>
          </form>
        </Card>
        <HorizontalSpacer spacing={4} />
      </div>

      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {errorMessage !== undefined && (
          <Modal open={errorMessage !== undefined} onClose={() => setErrorMessage(undefined)} closeIcon>
            <H2>Error</H2>
            <H3>{errorMessage}</H3>
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {leaveOrgModalOpen && (
          <Modal open={leaveOrgModalOpen} onClose={() => setLeaveOrgModalOpen(!leaveOrgModalOpen)} closeIcon>
            <LeaveOrgModalContent
              orgName={myOrganization?.name}
              onClose={() => setLeaveOrgModalOpen(false)}
              onClickLeaveOrg={onLeaveOrganization}
            />
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {deleteAccountModalOpen && (
          <Modal
            open={deleteAccountModalOpen}
            onClose={() => setDeleteAccountModalOpen(!deleteAccountModalOpen)}
            closeIcon
          >
            <DeleteAccountWarningModal
              onClose={() => setDeleteAccountModalOpen(false)}
              onClickDelete={onDeleteAccount}
            />
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {accountDeletedModal && (
          <Modal open={accountDeletedModal} onClose={() => setAccountDeletedModal(!accountDeletedModal)} closeIcon>
            <AccountDeletedModalContent />
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {resetPasswordModalOpen && (
          <Modal
            open={resetPasswordModalOpen}
            onClose={() => setResetPasswordModalOpen(!resetPasswordModalOpen)}
            closeIcon
          >
            <LoginModalContainer initialPage="reset" onCloseModal={() => setResetPasswordModalOpen(false)} />
          </Modal>
        )}
      </AnimatePresence>
    </div>
  )
}

export default AccountPage
